'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  TrendingUp,
  Bookmark,
  Lightbulb,
  DollarSign,
  LogOut,
  LayoutDashboard,
  Server,
  Clock,
  FileText,
  Bot,
  Cpu,
  Settings as SettingsIcon,
  BookOpen,
  Users,
  Network,
  Sparkles,
  Trophy,
  PlusCircle,
  ListOrdered,
  Activity,
  Database,
  Sliders,
  Search,
  BarChart3,
} from 'lucide-react';
import { useAuth, useLogout } from '@/hooks/use-auth';
import { TelegramLinkButton } from '@/components/telegram-link-button';
import { useWebSocketStore } from '@/stores/websocket-store';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { href: '/trends', label: 'Тренды', icon: TrendingUp },
  { href: '/bookmarks', label: 'Закладки', icon: Bookmark },
  { href: '/monetization', label: 'Монетизация', icon: Lightbulb },
  { href: '/forecasts', label: 'Прогнозы', icon: Sparkles },
  { href: '/costs', label: 'Расходы', icon: DollarSign },
] as const;

const BETTING_NAV_GROUPS = [
  {
    label: 'Основное',
    items: [
      { href: '/betting', label: 'Обзор', icon: LayoutDashboard, exact: true },
      { href: '/betting/new', label: 'Ввод матча', icon: PlusCircle },
      { href: '/betting/matches', label: 'Матчи', icon: ListOrdered },
    ],
  },
  {
    label: 'Настройки',
    items: [
      { href: '/betting/agents', label: 'Агенты', icon: Bot },
      { href: '/betting/mcp', label: 'MCP-серверы', icon: Server },
      { href: '/betting/models', label: 'Модели', icon: Sliders },
      { href: '/betting/sources', label: 'Источники', icon: Database },
    ],
  },
  {
    label: 'Анализ',
    items: [
      { href: '/betting/monitor', label: 'Мониторинг', icon: Activity },
      { href: '/betting/diagnostics', label: 'Диагностика', icon: Search },
      { href: '/betting/metrics', label: 'Метрики', icon: BarChart3 },
    ],
  },
] as const;

const ADMIN_NAV_GROUPS = [
  {
    label: 'Сбор данных',
    items: [
      { href: '/admin', label: 'Обзор', icon: LayoutDashboard, exact: true },
      { href: '/admin/mcp-servers', label: 'MCP-серверы', icon: Server },
      { href: '/admin/schedules', label: 'Расписания', icon: Clock },
      { href: '/admin/logs', label: 'Логи сбора', icon: FileText },
    ],
  },
  {
    label: 'AI',
    items: [
      { href: '/admin/agents', label: 'Агенты', icon: Bot },
      { href: '/admin/providers', label: 'Провайдеры', icon: Cpu },
      { href: '/admin/relationships', label: 'Схема связей', icon: Network },
    ],
  },
  {
    label: 'Система',
    items: [
      { href: '/admin/settings', label: 'Настройки', icon: SettingsIcon },
      { href: '/admin/budget', label: 'Бюджет', icon: DollarSign },
      { href: '/admin/wiki', label: 'Документация', icon: BookOpen },
      { href: '/admin/users', label: 'Пользователи', icon: Users },
    ],
  },
] as const;

export function Sidebar() {
  const pathname = usePathname();
  const { user } = useAuth();
  const logout = useLogout();
  const isAdmin = user?.role === 'admin';
  const newForecastCount = useWebSocketStore((s) => s.newForecastCount);
  const clearNewForecasts = useWebSocketStore((s) => s.clearNewForecasts);

  return (
    <aside className="flex h-screen w-60 flex-col border-r border-sidebar-border bg-sidebar">
      <div className="flex h-14 items-center px-6">
        <span className="text-lg font-bold text-sidebar-foreground">
          HuginnMuninn
        </span>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
        {NAV_ITEMS.map(({ href, label, icon: Icon }) => {
          const isActive = pathname.startsWith(href);
          const isForecasts = href === '/forecasts';
          const showBadge = isForecasts && newForecastCount > 0;
          return (
            <Link
              key={href}
              href={href}
              onClick={() => {
                if (isForecasts) clearNewForecasts();
              }}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {showBadge && (
                <span className="bg-purple-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                  {newForecastCount > 99 ? '99+' : newForecastCount}
                </span>
              )}
            </Link>
          );
        })}

        {/* Betting section — visible to all authenticated users */}
        <div className="border-t border-sidebar-border my-2" />
        <p className="px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">
          <Trophy className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          Ставки
        </p>
        {BETTING_NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="px-3 pt-2 text-xs text-muted-foreground">
              {group.label}
            </p>
            {group.items.map(({ href, label, icon: Icon, ...rest }) => {
              const exact = 'exact' in rest && rest.exact;
              const isActive = exact
                ? pathname === href
                : pathname.startsWith(href) && (href !== '/betting' || pathname === '/betting');
              return (
                <Link
                  key={href}
                  href={href}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Link>
              );
            })}
          </div>
        ))}

        {isAdmin && (
          <>
            <div className="border-t border-sidebar-border my-2" />
            <p className="px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">
              Администрирование
            </p>

            {ADMIN_NAV_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="px-3 pt-2 text-xs text-muted-foreground">
                  {group.label}
                </p>
                {group.items.map(({ href, label, icon: Icon, ...rest }) => {
                  const exact = 'exact' in rest && rest.exact;
                  const isActive = exact
                    ? pathname === href
                    : pathname.startsWith(href);
                  return (
                    <Link
                      key={href}
                      href={href}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                          : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </Link>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </nav>

      <div className="border-t border-sidebar-border px-3 py-3">
        {user && (
          <>
            <p className="mb-2 truncate px-3 text-xs text-muted-foreground">
              {user.email}
            </p>
            <TelegramLinkButton />
          </>
        )}
        <button
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <LogOut className="h-4 w-4" />
          Выйти
        </button>
      </div>
    </aside>
  );
}
